import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Box,
	IconButton,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useGet21ListQuery,
	useGetUserDetailQuery,
} from '@/api/user/userApiSlice';
import {
	User21Filter,
	UserSeries21PageResDTO,
} from '@/api/DTO/user/userList.interface';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Series21ProgressSummaryFilterSection from './components/Series21ProgressSummaryFilterSection';

export interface DeleteUserListDataGrid {
	updatedAt: string;
}

export default function Series21ProgressSummaryPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const { id } = useParams();

	const { data: userDetail }: any = useGetUserDetailQuery(id || '', {
		refetchOnMountOrArgChange: true,
	});

	const defaultPropertyFilter: User21Filter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		statuses: [],
		id: id || '',
	};
	const defaultValues: DefaultValues<User21Filter> = defaultPropertyFilter;
	const methods = useForm<User21Filter>({
		defaultValues,
	});
	const transformedSearchParams: User21Filter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'statuses',
			'id',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGet21ListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: UserSeries21PageResDTO[]) => {
			if (response == null) return;
			const newResponse: DeleteUserListDataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as User21Filter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('user.seriesTitleZh'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('user.seriesStatus'),
			flex: 21,
			renderCell: (params) => (
				<Chip
					color={params.row.published ? 'chipPrimary' : 'default'}
					label={params.row.published ? t('day21.on') : t('day21.off')}
				/>
			),
		},
		{
			field: 'count',
			headerName: t('user.userLockProgress'),
			flex: 21,
			renderCell: (params) => `${params.row.maxDay}/${params.row.singleCount}`,
		},
		{
			field: 'questionnaireFinishedStatus',
			headerName: t('user.questionnaireCompletionStatus'),
			flex: 21,
			renderCell: (params) =>
				typeof params.row.questionnaireFinishedStatus == 'boolean' ? (
					<Chip
						color={
							params.row.questionnaireFinishedStatus ? 'chipPrimary' : 'default'
						}
						label={
							params.row.questionnaireFinishedStatus
								? t('day21.completed')
								: t('day21.noCompleted')
						}
					/>
				) : (
					'-'
				),
		},
		{
			field: 'updatedAt',
			headerName: t('user.progressUpdateTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/user-manage/user-list/info/${id}/series-21/${params.row.seriesId}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('user.record21List')}({userDetail?.data.username},
					{userDetail?.data.nickname})
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<Series21ProgressSummaryFilterSection></Series21ProgressSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.seriesId}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
